import { useState, useMemo } from 'react';
import { RotateCcw, RefreshCw, ChevronDown, Check, Search } from 'lucide-react';
import { VisibilityState } from '@tanstack/react-table';
import { Button } from '@/core/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import { ActorSelector } from '@/core/components/ActorSelector';
import { Input } from '@/core/components/ui/input';
import { SlimLocation } from '../types/api-types';
import { DatePresetMenu } from './DatePresetMenu';
import { ExportMenu } from './ExportMenu';
import { ColumnFilter } from './ColumnFilter';
import { TextFilterInput } from './TextFilterInput';
import { Switch } from '@/core/components/ui/switch';
import { SESSION_STATUS_OPTIONS, SettlementFilter } from '../types/kiosk-session-types';
import { cn } from '@/core/lib/utils';

interface KioskSessionsToolbarProps {
  // Operator props (for distributors)
  isDistributor?: boolean;
  operators?: Array<{ operatorId: number; name: string }>;
  selectedOperatorId?: string;
  onOperatorChange?: (operatorId: string) => void;
  operatorsLoading?: boolean;

  // Location props
  ocpiLocations: SlimLocation[];
  selectedLocation: string;
  onLocationChange: (locationId: string) => void;

  // Sub-operator props (derived from the selected operator's actor hierarchy)
  subOperators?: Array<{ actorId: number; name: string }>;
  selectedSubOperatorId?: string;
  onSubOperatorChange?: (subOperatorId: string) => void;

  // Date filters
  dateRange: { from: string; to: string }; // YYYY-MM-DD format
  onDateRangeChange: (range: { from: string; to: string }) => void;

  // Grouping toggle
  groupByLocation: boolean;
  onGroupByLocationChange: (value: boolean) => void;

  // Search
  searchQuery: string;
  onSearchChange: (value: string) => void;

  // Session ID filter (dedicated, with server-side fallback)
  sessionId?: string;
  onSessionIdChange: (value?: string) => void;

  // Additional text filters
  last4Digits?: string;
  onLast4DigitsChange: (value?: string) => void;

  evseId?: string;
  onEvseIdChange: (value?: string) => void;

  connectorId?: string;
  onConnectorIdChange: (value?: string) => void;

  status?: string[];
  onStatusChange: (values: string[]) => void;

  transactionId?: string;
  onTransactionIdChange: (value?: string) => void;

  // Min energy filter
  minEnergyKwh?: string;
  onMinEnergyChange: (value?: string) => void;

  // Settlement filter
  settlement?: SettlementFilter;
  onSettlementChange: (value: SettlementFilter) => void;

  // Actions
  onRefresh: () => void;
  onReset: () => void;

  // Export
  columnVisibility: VisibilityState;
  availableColumns: Array<{ id: string; header: string }>;
  onExport: (visibility: VisibilityState) => void;

  // Column filter
  onColumnVisibilityChange: (visibility: VisibilityState) => void;

  // Session count
  sessionCount: number;

  // Loading state
  isLoading: boolean;
}

/**
 * OCPI Location selector with search and All Locations option
 */
function OcpiLocationSelector({
  locations,
  selectedLocationId,
  onLocationChange,
  disabled = false,
}: {
  locations: SlimLocation[];
  selectedLocationId: string;
  onLocationChange: (locationId: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredLocations = useMemo(() => {
    if (!search) return locations;
    const q = search.toLowerCase();
    return locations.filter(
      (loc) =>
        loc.name?.toLowerCase().includes(q) ||
        loc.id?.toLowerCase().includes(q) ||
        loc.city?.toLowerCase().includes(q) ||
        loc.address?.toLowerCase().includes(q)
    );
  }, [locations, search]);

  const selectedName = useMemo(() => {
    if (!selectedLocationId) return 'All Locations';
    const loc = locations.find((l) => l.id === selectedLocationId);
    return loc?.name || 'All Locations';
  }, [selectedLocationId, locations]);

  const handleSelect = (locId: string) => {
    onLocationChange(locId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">OCPI Location</span>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="w-[160px] h-8 justify-between font-normal border-[#E8E8E8] text-sm"
          >
            <span className="truncate">{selectedName}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          {/* Search */}
          <div className="flex items-center gap-2 px-2 py-2 border-b">
            <Search className="h-4 w-4 text-gray-400 shrink-0" />
            <Input
              type="text"
              placeholder="Search locations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          <div className="max-h-[300px] overflow-y-auto p-1">
            {/* All Locations */}
            <button
              className={cn(
                'flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-accent',
                !selectedLocationId && 'bg-accent/50'
              )}
              onClick={() => handleSelect('')}
            >
              <span className="font-medium">All Locations</span>
              {!selectedLocationId && <Check className="h-4 w-4 text-[#FFCD00] shrink-0" />}
            </button>

            {/* Location items */}
            {filteredLocations.map((loc) => {
              const isSelected = selectedLocationId === loc.id;
              return (
                <button
                  key={loc.id}
                  className={cn(
                    'flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-accent',
                    isSelected && 'bg-accent/50'
                  )}
                  onClick={() => handleSelect(loc.id)}
                >
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-medium truncate">{loc.name}</span>
                    <span className="text-xs text-gray-500 truncate">
                      {[loc.city, loc.address].filter(Boolean).join(', ') || loc.id}
                    </span>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-[#FFCD00] shrink-0 ml-2" />}
                </button>
              );
            })}

            {filteredLocations.length === 0 && (
              <div className="px-2 py-4 text-center text-sm text-gray-500">
                No locations found
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Sub-Operator selector with search and All Sub-Operators option
 */
function SubOperatorSelector({
  subOperators,
  selectedSubOperatorId,
  onSubOperatorChange,
  disabled = false,
}: {
  subOperators: Array<{ actorId: number; name: string }>;
  selectedSubOperatorId: string;
  onSubOperatorChange: (subOperatorId: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return subOperators;
    const q = search.toLowerCase();
    return subOperators.filter(
      (op) => op.name?.toLowerCase().includes(q) || op.actorId.toString().includes(q)
    );
  }, [subOperators, search]);

  const selectedName = useMemo(() => {
    if (!selectedSubOperatorId) return 'All Sub-Operators';
    const op = subOperators.find((o) => o.actorId.toString() === selectedSubOperatorId);
    return op?.name || 'All Sub-Operators';
  }, [selectedSubOperatorId, subOperators]);

  const handleSelect = (id: string) => {
    onSubOperatorChange(id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">Sub-Operator</span>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="w-[170px] h-8 justify-between font-normal border-[#E8E8E8] text-sm"
          >
            <span className="truncate">{selectedName}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <div className="flex items-center gap-2 px-2 py-2 border-b">
            <Search className="h-4 w-4 text-gray-400 shrink-0" />
            <Input
              type="text"
              placeholder="Search sub-operators..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          <div className="max-h-[300px] overflow-y-auto p-1">
            <button
              className={cn(
                'flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-accent',
                !selectedSubOperatorId && 'bg-accent/50'
              )}
              onClick={() => handleSelect('')}
            >
              <span className="font-medium">All Sub-Operators</span>
              {!selectedSubOperatorId && <Check className="h-4 w-4 text-[#FFCD00] shrink-0" />}
            </button>

            {filtered.map((op) => {
              const id = op.actorId.toString();
              const isSelected = selectedSubOperatorId === id;
              return (
                <button
                  key={id}
                  className={cn(
                    'flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-accent',
                    isSelected && 'bg-accent/50'
                  )}
                  onClick={() => handleSelect(id)}
                >
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-medium truncate">{op.name}</span>
                    <span className="text-xs text-gray-500 truncate">{id}</span>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-[#FFCD00] shrink-0 ml-2" />}
                </button>
              );
            })}

            {filtered.length === 0 && (
              <div className="px-2 py-4 text-center text-sm text-gray-500">
                No sub-operators found
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Three-row toolbar for Kiosk Sessions grid matching standard grid convention
 * Row 1: Primary filters (Date Range) + View Report + Reset
 * Row 2: Secondary search filters (Session ID search)
 * Row 3: View controls (Group by Location toggle) + session count + Export + Column Filter + Refresh
 */
export function KioskSessionsToolbar({
  isDistributor = false,
  operators = [],
  selectedOperatorId,
  onOperatorChange,
  operatorsLoading = false,
  ocpiLocations,
  selectedLocation,
  onLocationChange,
  subOperators = [],
  selectedSubOperatorId = '',
  onSubOperatorChange,
  dateRange,
  onDateRangeChange,
  groupByLocation,
  onGroupByLocationChange,
  searchQuery,
  onSearchChange,
  sessionId,
  onSessionIdChange,
  last4Digits,
  onLast4DigitsChange,
  evseId,
  onEvseIdChange,
  connectorId,
  onConnectorIdChange,
  status,
  onStatusChange,
  transactionId,
  onTransactionIdChange,
  minEnergyKwh,
  onMinEnergyChange,
  settlement,
  onSettlementChange,
  onRefresh,
  onReset,
  columnVisibility,
  availableColumns,
  onExport,
  onColumnVisibilityChange,
  sessionCount,
  isLoading
}: KioskSessionsToolbarProps) {
  const hasActiveFilters =
    searchQuery ||
    sessionId ||
    last4Digits ||
    evseId ||
    connectorId ||
    (status?.length ?? 0) > 0 ||
    transactionId ||
    groupByLocation ||
    minEnergyKwh ||
    (settlement && settlement !== 'all') ||
    selectedSubOperatorId ||
    selectedLocation !== 'all-locations';

  return (
    <div className="bg-white border-b border-[#E8E8E8] flex-shrink-0">
      {/* Row 1: Primary Context Filters - Operator + Location + Date + Actions */}
      <div className="flex flex-wrap items-end gap-3 px-4 py-2 border-b border-[#E8E8E8]">
        {/* Operator Selector (only for distributors) */}
        {isDistributor && operators.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Operator</span>
            <ActorSelector
              operators={operators}
              selectedOperatorId={selectedOperatorId || ''}
              onOperatorChange={onOperatorChange || (() => {})}
              isLoading={operatorsLoading}
              className="w-[160px] h-8 border-[#E8E8E8]"
            />
          </div>
        )}

        {/* Location Selector (OCPI locations) */}
        <OcpiLocationSelector
          locations={ocpiLocations}
          selectedLocationId={selectedLocation}
          onLocationChange={onLocationChange}
          disabled={isLoading}
        />

        {/* Sub-Operator Selector (hidden when the operator has no sub-operators) */}
        {subOperators.length > 0 && (
          <SubOperatorSelector
            subOperators={subOperators}
            selectedSubOperatorId={selectedSubOperatorId}
            onSubOperatorChange={onSubOperatorChange || (() => {})}
            disabled={isLoading}
          />
        )}

        {/* Period - Date Preset Menu */}
        <DatePresetMenu
          value={dateRange}
          onChange={onDateRangeChange}
        />

        {/* View Report Button - Nayax Yellow */}
        <Button
          onClick={onRefresh}
          disabled={isLoading}
          className="bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] font-medium h-8 px-4 mt-auto shadow-sm"
        >
          {isLoading ? 'Loading...' : 'View Report'}
        </Button>

        {/* Reset Filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-xs h-8 px-2 text-[#606060] hover:text-[#262626] mt-auto"
            disabled={isLoading}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {/* Row 2: Secondary Search Filters - Text Inputs */}
      <div className="flex flex-wrap items-end gap-3 px-4 py-2 border-b border-[#E8E8E8] bg-[#FAFAFA]">
        {/* Session ID Filter */}
        <TextFilterInput
          label="Session ID"
          value={sessionId}
          onChange={onSessionIdChange}
          onSubmit={onRefresh}
          placeholder="Session ID..."
          className="w-[150px]"
        />

        {/* Last 4 Digits Filter */}
        <TextFilterInput
          label="Last 4 Digits"
          value={last4Digits}
          onChange={onLast4DigitsChange}
          onSubmit={onRefresh}
          placeholder="1234"
          type="number"
          className="w-[100px]"
        />

        {/* EVSE ID Filter */}
        <TextFilterInput
          label="EVSE ID"
          value={evseId}
          onChange={onEvseIdChange}
          onSubmit={onRefresh}
          placeholder="EVSE..."
          className="w-[110px]"
        />

        {/* Connector ID Filter */}
        <TextFilterInput
          label="Connector"
          value={connectorId}
          onChange={onConnectorIdChange}
          onSubmit={onRefresh}
          placeholder="Connector..."
          className="w-[100px]"
        />

        {/* Status Filter - Multi-select */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Status</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-[130px] justify-between font-normal border-[#E8E8E8] text-sm"
              >
                <span className="truncate">
                  {(status?.length ?? 0) === 0
                    ? 'Standard'
                    : status!.length === 1
                    ? status![0]
                    : `Status (${status!.length})`}
                </span>
                <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[160px] p-1" align="start">
              {SESSION_STATUS_OPTIONS.map(option => {
                const selected = (status ?? []).includes(option);
                return (
                  <button
                    key={option}
                    className={cn(
                      'flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent',
                      selected && 'bg-accent/50'
                    )}
                    onClick={() => {
                      const current = status ?? [];
                      onStatusChange(
                        selected
                          ? current.filter(s => s !== option)
                          : [...current, option]
                      );
                    }}
                  >
                    <div className={cn(
                      'h-4 w-4 shrink-0 rounded-sm border border-gray-400 flex items-center justify-center',
                      selected && 'bg-[#FFCD00] border-[#FFCD00]'
                    )}>
                      {selected && <Check className="h-3 w-3 text-[#262626]" />}
                    </div>
                    {option}
                  </button>
                );
              })}
              {(status?.length ?? 0) > 0 && (
                <button
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-muted-foreground rounded hover:bg-accent border-t mt-1 pt-2"
                  onClick={() => onStatusChange([])}
                >
                  Clear
                </button>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Transaction ID Filter */}
        <TextFilterInput
          label="Transaction ID"
          value={transactionId}
          onChange={onTransactionIdChange}
          onSubmit={onRefresh}
          placeholder="Transaction ID..."
          className="w-[150px]"
        />

        {/* Min Energy Filter */}
        <TextFilterInput
          label="Min Energy (kWh)"
          value={minEnergyKwh}
          onChange={onMinEnergyChange}
          onSubmit={onRefresh}
          placeholder="e.g. 0.5"
          type="text"
          className="w-[110px]"
        />

        {/* Settlement Filter */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Settlement</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-[130px] justify-between font-normal border-[#E8E8E8] text-sm"
              >
                <span className="truncate">
                  {(settlement || 'all') === 'all'
                    ? 'All'
                    : settlement === 'settled'
                    ? 'Settled'
                    : 'Unsettled'}
                </span>
                <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[140px] p-1" align="start">
              {([
                { value: 'all', label: 'All' },
                { value: 'settled', label: 'Settled' },
                { value: 'no-settlement', label: 'Unsettled' },
              ] as const).map(option => (
                <button
                  key={option.value}
                  className={cn(
                    'flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-accent',
                    (settlement || 'all') === option.value && 'bg-accent/50'
                  )}
                  onClick={() => onSettlementChange(option.value)}
                >
                  {option.label}
                  {(settlement || 'all') === option.value && <Check className="h-4 w-4 text-[#FFCD00] shrink-0" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Row 3: View Controls + Actions */}
      <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-[#FAFAFA]">

        {/* Spacer */}
        <div className="flex-1" />

        {/* Session Count */}
        <span className="text-sm text-[#606060] font-medium">
          {sessionCount.toLocaleString()} {sessionCount === 1 ? 'session' : 'sessions'}
        </span>

        {/* Export Menu */}
        <ExportMenu
          columnVisibility={columnVisibility}
          availableColumns={availableColumns}
          onExport={onExport}
          disabled={sessionCount === 0 || isLoading}
        />

        {/* Column Filter */}
        <ColumnFilter
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
          availableColumns={availableColumns}
        />

        {/* Group by Location Toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="group-by-location"
            checked={groupByLocation}
            onCheckedChange={onGroupByLocationChange}
            className="data-[state=checked]:bg-[#FFCD00]"
          />
          <label
            htmlFor="group-by-location"
            className="text-sm text-[#262626] cursor-pointer"
          >
            Group by Location
          </label>
        </div>

        {/* Refresh Button */}
        <Button
          onClick={onRefresh}
          variant="outline"
          size="sm"
          className="bg-white text-gray-600 hover:text-gray-900 border-[#E8E8E8] hover:border-gray-300 rounded-md h-8 w-8 p-0 shadow-sm"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    </div>
  );
}
